import { useCallback, useEffect, useRef, useState } from "react";

/**
 * This set of utilities is used to provide a switch for focus styles.
 * Focus styles like outlines should only show up when the user is interacting with
 * the app using the keyboard. Once the mouse is used again, focus outlines should be
 * disabled.
 */

export const SHOW_FOCUS_MARKER = "lodestar--show-focus";

const getFocusModeFromBodyClassList = () =>
    typeof document !== "undefined" && document.body.classList.contains(SHOW_FOCUS_MARKER) ? "keyboard" : "mouse";

export interface Unsubscribe {
    (): void;
}

export type FocusMode = "keyboard" | "mouse";

export interface UseFocusModeOptions {
    /**
     * If false, the body listener (and therefore any updating) will be turned off.
     */
    isDisabled?: boolean;

    /**
     * If true, the listener will not cause any React re-renders, instead you will be able to
     * retrieve the value from the function getFocusMode which is returned by the hook.
     */
    passive?: boolean;
}

export interface UseFocusModeResult {
    /**
     * The current focusMode.
     */
    focusMode: FocusMode;

    /**
     * Gets the current focus mode at any time. This is generally in sync with focusMode, except
     * when passive is true.
     * @see UseFocusModeOptions['passive']
     */
    getFocusMode: () => FocusMode;

    /**
     * Sets the focus mode marker onto the body element. This will cause all instances of
     * useFocusMode to re-render with the new state.
     */
    setFocusMode: (newMode: FocusMode) => void;
}

/**
 * Hook that watches the document for key and mouse events to toggle between keyboard
 * and mouse modes.
 *
 * This hook assumes the initial mode to be mouse.
 */
export const useFocusMode = ({ isDisabled, passive = false }: UseFocusModeOptions = {}): UseFocusModeResult => {
    const [focusMode, setFocusMode] = useState<FocusMode>(getFocusModeFromBodyClassList);

    const passiveFocusModeRef = useRef<FocusMode>(focusMode);
    const getFocusModeFromPassiveRef = useCallback(() => passiveFocusModeRef.current, []);

    // Hooks up a MutationObserver that watches the body class. This means any change
    // to add/remove the class we care about will cause a re-render here.
    // This is good because many components could use this hook and we want them all
    // to pay attention.
    useEffect(() => {
        if (isDisabled) {
            return;
        }

        const handleUpdate = () => {
            const initialFocusMode = getFocusModeFromBodyClassList();
            passiveFocusModeRef.current = initialFocusMode;

            // only cause a React re-render if necessary.
            if (!passive) {
                setFocusMode(getFocusModeFromBodyClassList());
            }
        };

        // Do a quick update in case it has changed while we were disabled.
        handleUpdate();

        const obs = new MutationObserver(handleUpdate);
        obs.observe(document.body, {
            attributes: true,
            attributeFilter: ["class"],
        });
        return () => {
            obs.disconnect();
        };
    }, [isDisabled, passive]);

    return {
        focusMode,

        getFocusMode: getFocusModeFromPassiveRef,

        // This function needs to add/remove the marker class from the body. This allows the
        // mutation observer above to cause the re-render in ALL of the usages of these hooks,
        // not just this one.
        setFocusMode: useCallback<UseFocusModeResult["setFocusMode"]>((newMode) => {
            switch (newMode) {
                case "keyboard":
                    document.body.classList.add(SHOW_FOCUS_MARKER);
                    break;
                case "mouse":
                    document.body.classList.remove(SHOW_FOCUS_MARKER);
                    break;
            }
        }, []),
    };
};
